<template>
  <aside class="top-creator">
    <div class="top-box">
      <div class="title">Top Performing Creators</div>
      <div class="type-box">
        <Swiper :modules="modules" :slides-per-view="'auto'" :space-between="4" navigation class="button-swiper">
          <SwiperSlide v-for="(v, k) in button_list" :key="k" class="button-slide">
            <el-button
              :type="v.index === type ? 'default' : 'text'"
              @click="
                type = v.index;
                fetchData();
              "
            >
              {{ v.name }}
            </el-button>
          </SwiperSlide>
        </Swiper>
      </div>
      <Loading class="static" v-if="is_loading" />
      <div
        v-else-if="
          !is_loading &&
          data.length !== 0 &&
          ((type === 1 && sum_views !== 0) ||
            (type === 2 && sum_click !== 0) ||
            (type === 3 && sum_sales !== 0) ||
            (type === 4 && sum_sales_value !== 0) ||
            (type === 5 && sum_earnings !== 0))
        "
      >
        <div
          class="item"
          v-for="(item, index) in data"
          :key="index"
          @click="open(item)"
          :class="{
            hide:
              (type === 1 && item.views === 0) ||
              (type === 2 && item.click === 0) ||
              (type === 3 && item.sales === 0) ||
              (type === 4 && item.sales_value === 0) ||
              (type === 5 && item.earnings === 0),
          }"
        >
          <figure class="avatar">
            <img v-if="item.userProfile" :src="item.userProfile.avatar_full_url" @error="imageError($event)" />
          </figure>
          <div class="info">
            <div class="text">{{ item.first_name + ' ' + item.last_name }}</div>
            <div v-if="item.motom_handle" class="sub">@{{ item.motom_handle }}</div>
            <div v-else-if="item.tiktok_handle" class="sub">{{ item.tiktok_handle }}</div>
            <div v-else-if="item.instagram_handle" class="sub">@{{ item.instagram_handle }}</div>
          </div>
          <div v-if="type === 1" class="value">{{ formatNumber(item.views) }}</div>
          <div v-else-if="type === 2" class="value">{{ formatNumber(item.click) }}</div>
          <div v-else-if="type === 3" class="value">{{ formatNumber(item.sales) }}</div>
          <div v-else-if="type === 4" class="value">${{ formatMoney(item.sales_value) }}</div>
          <div v-else-if="type === 5" class="value">${{ formatMoney(item.earnings) }}</div>
        </div>
      </div>
      <div v-else-if="!is_loading">
        <NoData :image="require('@/assets/images/cart.png')" text="" title="No data available" />
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { useStore } from 'vuex';
import { ElButton } from 'element-plus';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
import Loading from '@/components/ui/Loading.vue';
import formatMoneyUtil from '@/utils/formatMoney';
import moment from 'moment';
import NoData from '@/components/creator/NoData.vue';
import { dataSourceToApi } from '@/utils/SwitchFun.js';

// Get instance for global properties
const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const EventBus = app.appContext.config.globalProperties.EventBus;
const store = useStore();

// Default avatar for error handling
const defaultAvatar = require('@/assets/images/user.png');

// Swiper modules
const modules = [Navigation];

// Props
const props = defineProps({
  start_date: {
    type: [Date, String],
    default: null,
  },
  end_date: {
    type: [Date, String],
    default: null,
  },
  selected_creator_tags: {
    type: Array,
    default: () => [],
  },
  creator_tag_operation_type: {
    type: String,
    default: 'or',
  },
});

// Emits
const emit = defineEmits(['top_data']);

// Reactive data
const is_loading = ref(true);
const type = ref(1);
const sum_views = ref(0);
const sum_click = ref(0);
const sum_sales = ref(0);
const sum_sales_value = ref(0);
const sum_earnings = ref(0);
const data = ref([]);
const is_link_only = computed(() => {
  return store.state.user_data?.merchant_account.platform_type === 'linkonly';
});
const button_list = ref([
  ...!is_link_only.value ? [{ name: 'Views', index: 1 }] : [],
  { name: 'Clicks', index: 2 },
  { name: 'Sales', index: 3 },
  { name: 'Sales Value', index: 4 },
]);
const sortlink_data = ref('all');

// Computed
const permission_creator = computed(() => {
  return store.state.user_data?.permission_list?.creatorManagement;
});

// Methods
const formatNumber = (num) => {
  return num ? num.toLocaleString('en-US') : '--';
};

const formatMoney = (value) => {
  return formatMoneyUtil(value);
};

const fetchData = () => {
  if (store.state.user_data.merchant_account_id) {
    is_loading.value = true;
    let s = moment(props.start_date).format('YYYY-MM-DD');
    let e = moment(props.end_date).format('YYYY-MM-DD');
    let sort_key = '';

    switch (type.value) {
      case 1:
        sort_key = 'views';
        break;
      case 2:
        sort_key = 'click';
        break;
      case 3:
        sort_key = 'sales';
        break;
      case 4:
        sort_key = 'sales_value';
        break;
      case 5:
        sort_key = 'earnings';
        break;
      default:
        break;
    }

    let api = `/analytics/top-performing-creators?start_date=${s}&end_date=${e}&sort_key=${sort_key}&is_include_shortlink_data=${dataSourceToApi(sortlink_data.value)}`;

    // Add creator tag filters
    if (props.selected_creator_tags && props.selected_creator_tags.length > 0) {
      props.selected_creator_tags.forEach((tag) => {
        api += `&creator_tag_ids[]=${tag.id}`;
      });
      api += `&operation_type=${props.creator_tag_operation_type}`;
    }

    ajax(api, {
      method: 'get',
      withCredentials: true,
    }).then((res) => {
      // Reset sums
      sum_views.value = 0;
      sum_click.value = 0;
      sum_sales.value = 0;
      sum_sales_value.value = 0;
      sum_earnings.value = 0;

      res.data.forEach((item) => {
        sum_views.value = sum_views.value + item.views;
        sum_click.value = sum_click.value + item.click;
        sum_sales.value = sum_sales.value + item.sales;
        sum_sales_value.value = sum_sales_value.value + item.sales_value;
      });

      data.value = res.data;
      is_loading.value = false;
      emit('top_data', { type: 'top-creator', data: true });
    });
  }
};

const open = (rowData) => {
  let d = {
    user_id: rowData.id,
    start_date: props.start_date,
    end_date: props.end_date,
  };
  EventBus.emit('open-creator-modal', d);
};

const imageError = (event) => {
  event.target.src = defaultAvatar;
};

// Lifecycle
onMounted(() => {
  button_list.value.push({ name: 'Earnings', index: 5 });
  sortlink_data.value = store.state.save_sortlink_data;
  fetchData();
});

// Expose methods for parent component
defineExpose({
  fetchData,
});
</script>

<style lang="scss">
.top-creator {
  .top-box {
    padding: 12px;
    border: 1px solid var(--black-20);
    border-radius: 10px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

    .creator-no-data img {
      width: 120px;
    }

    .title {
      font: var(--font-demi-20);
      color: var(--text-primary);
      display: flex;
      align-items: center;

      svg {
        width: 16px;
        height: 16px;
        margin-right: 6px;
      }
    }

    .type-box {
      margin-top: 8px;
      display: flex;
      position: relative;

        .swiper-button-next,
        .swiper-button-prev{
          display: none;
        }

      .button-slide {
        width: auto !important;
      }

      .el-button {
        font: var(--font-fg-14);
        padding: 0 12px;
      }
    }

    hr {
      border-top: 1px solid var(--black-20);
      overflow: hidden;
      height: 0px;
      border-bottom: 0;
    }

    .item {
      margin-top: 8px;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      &.hide {
        display: none;
      }

      &:hover {
        background: var(--bg-08);
      }

      .avatar {
        margin-right: 8px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid var(--black-40);
        }
      }

      .info {
        flex: 1;

        .text {
          font: var(--font-fg-12);
          color: var(--black-90);
        }

        .sub {
          font-weight: 500;
          font-size: 12px;
          line-height: 14px;
          color: var(--black-50);
        }
      }
    }
  }
}
</style>
